import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Fab,
  Collapse,
  CircularProgress,
  Button,
  Chip,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../utils/apiClient';
import { toast } from 'react-toastify';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm your meal ordering assistant. I can help you check available meal slots and place orders. What would you like to do today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const today = new Date().toDateString();
    const storedDate = localStorage.getItem('chatSessionDate');
    const storedCount = parseInt(localStorage.getItem('chatSessionCount') || '0');
    
    if (storedDate !== today) {
      localStorage.setItem('chatSessionDate', today);
      localStorage.setItem('chatSessionCount', '1');
      setSessionCount(1);
    } else {
      setSessionCount(storedCount);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || sessionEnded) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await apiClient.post('/chatbot/chat', {
        message: inputMessage,
        conversationHistory: conversationHistory,
        sessionCount: sessionCount
      });

      const botResponse = response.data.response;
      
      // Check for session end
      if (response.data.sessionEnded) {
        setSessionEnded(true);
        const botMessage = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date(),
          isSessionEnd: true
        };
        setMessages(prev => [...prev, botMessage]);
        return;
      }
      
      // Check if bot wants to place an order
      if (botResponse.startsWith('PLACE_ORDER:')) {
        const orderParts = botResponse.split(':');
        if (orderParts.length === 4) {
          const [, slotName, mealType, reason] = orderParts;
          await handlePlaceOrder(slotName, mealType, reason);
        }
      } else if (botResponse.startsWith('ADMIN_ORDER:')) {
        const orderParts = botResponse.split(':');
        if (orderParts.length === 5) {
          const [, targetUserEmail, slotName, mealType, reason] = orderParts;
          await handlePlaceOrder(slotName, mealType, reason, targetUserEmail);
        }
      } else {
        const botMessage = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date(),
          slotsAvailable: response.data.slotsAvailable,
          availableSlots: response.data.availableSlots,
          existingOrders: response.data.existingOrders
        };

        setMessages(prev => [...prev, botMessage]);
      }

      // Update conversation history
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: inputMessage },
        { role: 'assistant', content: botResponse }
      ]);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "Sorry, I'm having trouble processing your request. Please try again.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaceOrder = async (slotName, mealType, reason, targetUserEmail = null) => {
    try {
      const response = await apiClient.post('/chatbot/place-order', {
        slotName,
        mealType,
        reason,
        targetUserEmail
      });

      const successMessage = {
        id: Date.now() + 1,
        text: response.data.message,
        sender: 'bot',
        timestamp: new Date(),
        isOrderConfirmation: true
      };

      setMessages(prev => [...prev, successMessage]);
      toast.success('Order placed successfully!');
      
      // End session after successful order
      setSessionEnded(true);
      localStorage.setItem('chatSessionCount', (sessionCount + 1).toString());
      
      // Close chat after 3 seconds
      setTimeout(() => {
        setIsOpen(false);
        // Reset for next session
        setMessages([
          {
            id: 1,
            text: "Hi! I'm your meal ordering assistant. I can help you check available meal slots and place orders. What would you like to do today?",
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
        setConversationHistory([]);
        setSessionEnded(false);
      }, 3000);

    } catch (error) {
      console.error('Error placing order:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: error.response?.data?.message || "Sorry, I couldn't place your order. Please try again.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to place order');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    "Show available meals",
    "What slots are open?",
    "I want to order food",
    "Help me place an order"
  ];

  const handleQuickAction = (action) => {
    setInputMessage(action);
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <Fab
        color="primary"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          background: 'linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #16B1C8 0%, #0e7490 100%)',
          }
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              bottom: 90,
              right: 20,
              zIndex: 999,
              width: '350px',
              height: '500px'
            }}
          >
            <Paper
              elevation={8}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  background: 'linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <BotIcon />
                <Typography variant="h6" fontWeight={600}>
                  Meal Assistant
                </Typography>
              </Box>

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                      mb: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        maxWidth: '80%',
                        flexDirection: message.sender === 'user' ? 'row-reverse' : 'row'
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          background: message.sender === 'user' 
                            ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
                            : 'linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)'
                        }}
                      >
                        {message.sender === 'user' ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
                      </Avatar>
                      
                      <Paper
                        elevation={2}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: message.sender === 'user'
                            ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
                            : message.isOrderConfirmation
                            ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                          color: message.sender === 'user' || message.isOrderConfirmation ? 'white' : 'inherit',
                          border: message.sender === 'bot' ? '1px solid rgba(32, 213, 213, 0.2)' : 'none'
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.text}
                        </Typography>
                        
                        {message.existingOrders && message.existingOrders.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: '#f57c00' }}>
                              Existing Orders:
                            </Typography>
                            {message.existingOrders.map((slot, index) => (
                              <Chip
                                key={index}
                                label={slot}
                                size="small"
                                sx={{ 
                                  mr: 0.5, 
                                  mb: 0.5,
                                  background: '#fff3e0',
                                  color: '#f57c00',
                                  border: '1px solid #ffcc02'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        
                        {message.availableSlots && message.availableSlots.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                              Available Slots:
                            </Typography>
                            {message.availableSlots.map((slot, index) => (
                              <Chip
                                key={index}
                                label={`${slot.name} (${slot.startTime}-${slot.endTime})`}
                                size="small"
                                sx={{ 
                                  mr: 0.5, 
                                  mb: 0.5,
                                  background: 'rgba(32, 213, 213, 0.1)',
                                  color: '#16B1C8'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Box>
                ))}
                
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          background: 'linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)'
                        }}
                      >
                        <BotIcon fontSize="small" />
                      </Avatar>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                          border: '1px solid rgba(32, 213, 213, 0.2)'
                        }}
                      >
                        <CircularProgress size={16} sx={{ color: '#20d5d5' }} />
                      </Paper>
                    </Box>
                  </Box>
                )}
                
                <div ref={messagesEndRef} />
              </Box>

              {/* Quick Actions */}
              {messages.length === 1 && (
                <Box sx={{ p: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                  <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
                    Quick actions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {quickActions.map((action, index) => (
                      <Chip
                        key={index}
                        label={action}
                        size="small"
                        onClick={() => handleQuickAction(action)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            background: 'rgba(32, 213, 213, 0.1)'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Session limit warning */}
              {sessionEnded && (
                <Alert severity="info" sx={{ m: 1 }}>
                  Chat session ended. Please start a new conversation if needed.
                </Alert>
              )}
              
              {/* Input */}
              <Box
                sx={{
                  p: 1,
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  display: 'flex',
                  gap: 1
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading || sessionEnded}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#20d5d5',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#16B1C8',
                      },
                    }
                  }}
                />
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading || sessionEnded}
                  sx={{
                    background: 'linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16B1C8 0%, #0e7490 100%)',
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#bdbdbd'
                    }
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;